import React, {useEffect, useMemo, useState} from 'react';
import '../styles/shotVideo.css';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';
import * as actions from '../store/actions';
import * as postData from '../data/posts_data';
import {Helmet} from 'react-helmet';
import {FiArrowLeft, FiChevronLeft, FiChevronRight, FiEdit2, FiFilm, FiImage, FiMusic, FiPlus, FiSearch, FiSend, FiTag, FiVideo, FiX} from 'react-icons/fi';
import PhotoEditor from '../comps/photoEditor';
import {MAX_POST_PHOTOS, POST_IMAGE_SEP} from '../utils/postImages';
import {startCreateUpload} from '../utils/createUpload';
import {useCreateUpload} from '../hooks/useCreateUpload';
import {isMusicPost} from '../utils/mediaRoute';
import {withSoundTag} from '../utils/sound';
import UserAvatar from '../comps/userAvatar';
import PostModel from '../model/posts';

const contentTypes = [
  {value: 's', label: 'Short video', icon: <FiVideo />},
  {value: 'l', label: 'Long video', icon: <FiFilm />},
  {value: 'a', label: 'Music', icon: <FiMusic />},
  {value: 'i', label: 'Image post', icon: <FiImage />}
];

const toSound = (row) => {
  if (!row || row.msg || !row.id) return null;
  return new PostModel(
    row.id,
    row.image || row.content,
    row.title,
    row.type,
    row.userid,
    row.tages,
    row.disc || row.discrption || row.description,
    row.time,
    row.thumbnail,
    row.vCount || row.views,
    row.comm || row.vcomments,
    row.pCount || row.vlikes,
    row.dCount || row.vdislikes,
    row.userImg,
    row.email,
    row.userLink
  );
};

const popularity = (item) => (Number(item && item.views) || 0) + ((Number(item && item.vlikes) || 0) * 3);
const isImageFile = (file) => Boolean(file && String(file.type || '').startsWith('image/'));
const isAudioFile = (file) => Boolean(file && String(file.type || '').startsWith('audio/'));

const ShotVideo = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector(state => state.authReduse);
  const userPosts = useSelector(state => state.PostReducer.userPosts) || [];
  const feedLong = useSelector(state => state.PostReducer.followLong) || [];
  const feedShort = useSelector(state => state.PostReducer.followShort) || [];
  const uploadJob = useCreateUpload();
  const [media, setMedia] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [photoIndex, setPhotoIndex] = useState(0);
  const [editingPhoto, setEditingPhoto] = useState(null);
  const [thumbnail, setThumbnail] = useState(null);
  const [title, setTitle] = useState('');
  const [type, setType] = useState('s');
  const [tags, setTags] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');
  const [mediaPreview, setMediaPreview] = useState('');
  const [audioPreview, setAudioPreview] = useState('');
  const [thumbPreview, setThumbPreview] = useState('');
  const [photoPreviews, setPhotoPreviews] = useState([]);
  const [soundId, setSoundId] = useState('');
  const [soundQuery, setSoundQuery] = useState('');
  const [popularSounds, setPopularSounds] = useState([]);
  const [foundSounds, setFoundSounds] = useState([]);
  const [soundBusy, setSoundBusy] = useState(false);
  const isImagePost = type === 'i';
  const isMusic = type === 'a';
  const isShort = type === 's';

  useEffect(() => {
    dispatch(postData.get_posts(0, 8)).catch(() => {});
    dispatch(postData.get_my_posts()).catch(() => {});
  }, [dispatch]);

  useEffect(() => {
    if (!media) {
      setMediaPreview('');
      setAudioPreview('');
      return undefined;
    }
    const url = URL.createObjectURL(media);
    if (isAudioFile(media)) {
      setAudioPreview(url);
      setMediaPreview('');
    } else if (isImageFile(media)) {
      setMediaPreview(url);
      setAudioPreview('');
    } else {
      setMediaPreview('');
      setAudioPreview('');
      URL.revokeObjectURL(url);
      return undefined;
    }
    return () => URL.revokeObjectURL(url);
  }, [media]);

  useEffect(() => {
    if (!thumbnail || !isImageFile(thumbnail)) {
      setThumbPreview('');
      return undefined;
    }
    const url = URL.createObjectURL(thumbnail);
    setThumbPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [thumbnail]);

  useEffect(() => {
    const urls = photos.map(file => URL.createObjectURL(file));
    setPhotoPreviews(urls);
    return () => urls.forEach(url => URL.revokeObjectURL(url));
  }, [photos]);

  useEffect(() => {
    if (photoIndex >= photos.length) setPhotoIndex(Math.max(photos.length - 1, 0));
  }, [photos, photoIndex]);

  const fallbackMusic = useMemo(() => {
    const seen = new Set();
    return [...userPosts, ...feedShort, ...feedLong]
      .filter((item) => {
        if (!item || !item.id || seen.has(item.id)) return false;
        const shortOrMusic = isMusicPost(item) || String(item.type) === 's';
        if (!shortOrMusic) return false;
        seen.add(item.id);
        return true;
      })
      .sort((a, b) => popularity(b) - popularity(a));
  }, [userPosts, feedLong, feedShort]);

  useEffect(() => {
    let live = true;
    fetch('/api/posts/sounds/popular')
      .then(async (res) => {
        const type = res.headers.get('content-type') || '';
        if (!res.ok || !type.includes('json')) return [];
        return res.json();
      })
      .then((rows) => {
        if (!live) return;
        const list = (Array.isArray(rows) ? rows : []).map(toSound).filter(Boolean);
        setPopularSounds(list);
      })
      .catch(() => {
        if (live) setPopularSounds([]);
      });
    return () => { live = false; };
  }, []);

  useEffect(() => {
    const q = soundQuery.trim();
    if (!isShort || q.length < 1) {
      setFoundSounds([]);
      setSoundBusy(false);
      return undefined;
    }
    let live = true;
    setSoundBusy(true);
    const timer = setTimeout(() => {
      const needle = q.toLowerCase();
      const localHits = fallbackMusic.filter(item => String(item.title || '').toLowerCase().includes(needle));
      fetch(`/api/posts/sounds/search/${encodeURIComponent(q)}`)
        .then(async (res) => {
          const type = res.headers.get('content-type') || '';
          const rows = res.ok && type.includes('json') ? await res.json().catch(() => []) : [];
          const fromApi = (Array.isArray(rows) ? rows : []).map(toSound).filter(Boolean);
          const seen = new Set(fromApi.map(item => String(item.id)));
          return [...fromApi, ...localHits.filter(item => !seen.has(String(item.id)))];
        })
        .then((list) => {
          if (!live) return;
          setFoundSounds(list);
          setSoundBusy(false);
        })
        .catch(() => {
          if (!live) return;
          setFoundSounds(localHits);
          setSoundBusy(false);
        });
    }, 280);
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [soundQuery, isShort, fallbackMusic]);

  const searching = soundQuery.trim().length > 0;
  const recommendedSounds = useMemo(() => {
    if (searching) return foundSounds;
    const list = popularSounds.length ? popularSounds : fallbackMusic;
    return list.slice(0, 5);
  }, [searching, foundSounds, popularSounds, fallbackMusic]);

  const selectType = (value) => {
    setType(value);
    setError('');
    setSoundId('');
    setSoundQuery('');
    if (value === 'i') setMedia(null);
    else {
      setPhotos([]);
      setPhotoIndex(0);
    }
    if (value !== 'a' && media && isAudioFile(media)) setMedia(null);
    if (value === 'a' && media && !isAudioFile(media)) setMedia(null);
  };

  const addPhotos = (event) => {
    const incoming = Array.from(event.target.files || []).filter(isImageFile);
    if (!incoming.length) return;
    const start = photos.length;
    const next = [...photos, ...incoming].slice(0, MAX_POST_PHOTOS);
    setPhotos(next);
    setPhotoIndex(start);
    if (next[start]) setEditingPhoto({ index: start, file: next[start] });
    event.target.value = '';
  };

  const removePhoto = (index) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
    if (editingPhoto && editingPhoto.index === index) setEditingPhoto(null);
  };

  const applyEditedPhoto = (file) => {
    if (!editingPhoto) return;
    const index = editingPhoto.index;
    setPhotos(prev => prev.map((item, i) => (i === index ? file : item)));
    setPhotoIndex(index);
    setEditingPhoto(null);
  };

  const postHandle = async () => {
    if (isImagePost) {
      if (!photos.length || title.trim().length < 3 || description.trim().length < 3) {
        setError('Add at least one photo, a title, and a description before posting.');
        return;
      }
    } else if (!media || !thumbnail || title.trim().length < 3 || description.trim().length < 3) {
      setError(isMusic
        ? 'Add a music file, cover image, title, and description before posting.'
        : 'Add a video, cover image, title, and description before posting.');
      return;
    } else if (isMusic && !isAudioFile(media)) {
      setError('Music posts need an audio file, not a video.');
      return;
    }
    setError('');
    if (uploadJob && (uploadJob.status === 'uploading' || uploadJob.status === 'publishing')) {
      navigate('/profile');
      return;
    }
    const savedTags = isShort ? withSoundTag(tags.trim(), soundId) : tags.trim();
    const files = isImagePost ? photos : [media, thumbnail];
    const previewFile = isImagePost ? photos[0] : (thumbnail || media);
    startCreateUpload({
      files,
      previewFile,
      title: title.trim(),
      type,
      publish: async (names) => {
        let imageName = '';
        let coverName = '';
        if (isImagePost) {
          imageName = names.join(POST_IMAGE_SEP);
          coverName = names[0];
        } else {
          imageName = names[0];
          coverName = names[1];
        }
        await dispatch(actions.add_to_posts(1, imageName, type, title.trim(), savedTags, description.trim(), coverName, 1));
      }
    }).catch(() => {});
    navigate('/profile');
  };

  const currentPreview = photoPreviews[photoIndex];
  const captionHint = isImagePost ? 'Tell people about your photos…' : isMusic ? 'Tell people about this track…' : 'Tell people about your video…';

  return (
    <main className="createPostPage">
      <Helmet>
        <title>Create a post | Pinweelo</title>
        <meta name="theme-color" content="#08090d" />
        <meta name="description" content="Create and share a video, music, or image post" />
      </Helmet>
      <section className="createPostCard" aria-label="Create a post">
        <header className="createPostHeader createPostReveal">
          <button className="createPostBack" type="button" aria-label="Go back" onClick={() => navigate(-1)}>
            <FiArrowLeft />
          </button>
          <h1>Create post</h1>
          <button className="createPostSubmit" type="button" onClick={postHandle} disabled={!!uploadJob && uploadJob.status !== 'done' && uploadJob.status !== 'error'}>
            Post
          </button>
        </header>

        <div className="createPostAuthor createPostReveal">
          <UserAvatar src={user.photo} name={user.name || user.email} alt="" />
          <div>
            <strong>{user.name || user.email || 'Your profile'}</strong>
            <small>Share with everyone</small>
          </div>
        </div>

        <label className="createPostCaption createPostReveal">
          <span>What are you creating?</span>
          <textarea
            value={description}
            onChange={event => setDescription(event.target.value)}
            maxLength="1000"
            placeholder={captionHint}
          />
          <small>{description.length}/1000</small>
        </label>

        <div className="createPostFields createPostReveal">
          <label>
            <span>Title</span>
            <input value={title} onChange={event => setTitle(event.target.value)} maxLength="60" placeholder="Give your post a title" />
          </label>
          <label>
            <span><FiTag /> Tags</span>
            <input value={tags} onChange={event => setTags(event.target.value)} maxLength="100" placeholder="music, travel, tutorial" />
          </label>
        </div>

        <fieldset className="createPostTypes createPostReveal">
          <legend>Content type</legend>
          <div>
            {contentTypes.map(item => (
              <button
                key={item.value}
                type="button"
                className={type === item.value ? 'isSelected' : ''}
                onClick={() => selectType(item.value)}
              >
                {item.icon}
                <span>{item.label}</span>
              </button>
            ))}
          </div>
        </fieldset>

        {isShort && (
          <section className="createSoundPicker createPostReveal" aria-label="Choose sound">
            <h2>Sound</h2>
            <p>Use original audio, or pick one of the most popular tracks.</p>
            <button type="button" className={!soundId ? 'isSelected' : ''} onClick={() => setSoundId('')}>
              Original sound
            </button>
            <label className="createSoundSearch">
              <FiSearch />
              <input
                type="search"
                value={soundQuery}
                onChange={event => setSoundQuery(event.target.value)}
                placeholder="Search for a sound"
                maxLength="80"
                autoComplete="off"
              />
            </label>
            <small className="createSoundLabel">{searching ? 'Search results' : 'Popular sounds'}</small>
            <div className="createSoundRow">
              {recommendedSounds.map(track => (
                <button
                  key={track.id}
                  type="button"
                  className={soundId === String(track.id) ? 'isSelected' : ''}
                  onClick={() => setSoundId(String(track.id))}
                >
                  <img src={`/api/uploads/${track.thumbnail}`} alt="" />
                  <span>{track.title || 'Music'}</span>
                </button>
              ))}
            </div>
            {soundBusy ? (
              <small>Searching…</small>
            ) : searching && !recommendedSounds.length ? (
              <small>No sounds match that search.</small>
            ) : !searching && !recommendedSounds.length ? (
              <small>No popular sounds yet. Upload music, or use original sound.</small>
            ) : null}
          </section>
        )}

        <section
          key={isImagePost ? 'image-media' : isMusic ? 'music-media' : 'video-media'}
          className={`createPostMedia createPostReveal${isImagePost ? ' isImagePost' : ''}`}
          aria-label="Add media"
        >
          {isImagePost ? (
            <div className="imageComposer">
              {photos.length ? (
                <div className="imageComposerStage">
                  {currentPreview && <img src={currentPreview} alt="" />}
                  {photos.length > 1 && (
                    <>
                      <button type="button" className="imageComposerNav isPrev" aria-label="Previous photo" onClick={() => setPhotoIndex((photoIndex - 1 + photos.length) % photos.length)}>
                        <FiChevronLeft />
                      </button>
                      <button type="button" className="imageComposerNav isNext" aria-label="Next photo" onClick={() => setPhotoIndex((photoIndex + 1) % photos.length)}>
                        <FiChevronRight />
                      </button>
                    </>
                  )}
                  <span className="imageComposerCount">{photoIndex + 1}/{photos.length}</span>
                  <button
                    type="button"
                    className="imageComposerEdit"
                    onClick={() => photos[photoIndex] && setEditingPhoto({ index: photoIndex, file: photos[photoIndex] })}
                  >
                    <FiEdit2 />
                    Edit
                  </button>
                </div>
              ) : (
                <label className="mediaPicker imageComposerEmpty">
                  <FiImage />
                  <strong>Add photos</strong>
                  <small>Select up to {MAX_POST_PHOTOS} photos</small>
                  <input type="file" accept="image/*" multiple onChange={addPhotos} />
                </label>
              )}
              <div className="imageComposerStrip">
                {photoPreviews.map((src, index) => (
                  <div key={`${photos[index]?.name}-${index}`} className={`imageComposerThumb${index === photoIndex ? ' isActive' : ''}`}>
                    <button type="button" onClick={() => setPhotoIndex(index)}>
                      <img src={src} alt="" />
                    </button>
                    <button type="button" className="imageComposerRemove" aria-label="Remove photo" onClick={() => removePhoto(index)}>
                      <FiX />
                    </button>
                  </div>
                ))}
                {photos.length > 0 && photos.length < MAX_POST_PHOTOS && (
                  <label className="imageComposerAdd">
                    <FiPlus />
                    <input type="file" accept="image/*" multiple onChange={addPhotos} />
                  </label>
                )}
              </div>
            </div>
          ) : (
            <>
              <label className={media ? 'mediaPicker hasFile' : 'mediaPicker'}>
                {mediaPreview ? <img className="mediaPickerPreview" src={mediaPreview} alt="" /> : isMusic ? <FiMusic /> : <FiVideo />}
                <strong>{media ? media.name : isMusic ? 'Add music file' : 'Add video'}</strong>
                <small>{media ? 'Tap to replace' : isMusic ? 'MP3, WAV, M4A, or another audio file' : 'MP4, MOV, or a video file'}</small>
                {audioPreview ? <audio className="mediaPickerAudio" src={audioPreview} controls /> : null}
                <input
                  type="file"
                  accept={isMusic ? 'audio/*' : 'video/*'}
                  onChange={event => setMedia(event.target.files[0] || null)}
                />
              </label>
              <label className={thumbnail ? 'mediaPicker hasFile' : 'mediaPicker'}>
                {thumbPreview ? <img className="mediaPickerPreview" src={thumbPreview} alt="" /> : <FiImage />}
                <strong>{thumbnail ? thumbnail.name : isMusic ? 'Add cover art' : 'Add cover image'}</strong>
                <small>{thumbnail ? 'Tap to replace' : 'A thumbnail for your post'}</small>
                <input type="file" accept="image/*" onChange={event => setThumbnail(event.target.files[0] || null)} />
              </label>
            </>
          )}
        </section>

        {error && <p className="createPostError" role="alert">{error}</p>}
        {editingPhoto && editingPhoto.file && (
          <PhotoEditor
            file={editingPhoto.file}
            onCancel={() => setEditingPhoto(null)}
            onApply={applyEditedPhoto}
          />
        )}
        <button className="createPostBottomSubmit createPostReveal" type="button" onClick={postHandle} disabled={!!uploadJob && uploadJob.status !== 'done' && uploadJob.status !== 'error'}>
          <FiSend /> Create post
        </button>
      </section>
    </main>
  );
};

export default ShotVideo;
